import { createTheme, MantineColorsTuple, rem } from '@mantine/core';

// DeploySense brand colors - Navy blue (primary)
const brand: MantineColorsTuple = [
  '#e8f4fc',
  '#d1e5f6',
  '#a3caed',
  '#6eade3',
  '#4494da',
  '#2a84d4',
  '#1a365d', // Main dark navy
  '#152d4e',
  '#102440',
  '#0b1a2e',
];

// DeploySense accent color - Teal/Cyan
const accent: MantineColorsTuple = [
  '#e6fcfc',
  '#c3f7f7',
  '#87eeee',
  '#4de4e4',
  '#22d3d3',
  '#0cb8b8',
  '#0891b2', // Main teal
  '#077a96',
  '#06617a',
  '#044a5e',
];

// Status colors for task states
const success: MantineColorsTuple = [
  '#e6fcf5',
  '#c3fae8',
  '#96f2d7',
  '#63e6be',
  '#38d9a9',
  '#20c997',
  '#12b886',
  '#0ca678',
  '#099268',
  '#087f5b',
];

const warning: MantineColorsTuple = [
  '#fff9db',
  '#fff3bf',
  '#ffec99',
  '#ffe066',
  '#ffd43b',
  '#fcc419',
  '#fab005',
  '#f59f00',
  '#f08c00',
  '#e67700',
];

const danger: MantineColorsTuple = [
  '#fff5f5',
  '#ffe3e3',
  '#ffc9c9',
  '#ffa8a8',
  '#ff8787',
  '#ff6b6b',
  '#fa5252',
  '#f03e3e',
  '#e03131',
  '#c92a2a',
];

export const theme = createTheme({
  primaryColor: 'brand',
  primaryShade: 6,

  colors: {
    brand,
    accent,
    success,
    warning,
    danger,
  },

  // Apple-style fonts: SF Pro (system) with Inter fallback
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontFamilyMonospace: '"SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',

  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", Inter, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(32), lineHeight: '1.2' },
      h2: { fontSize: rem(24), lineHeight: '1.3' },
      h3: { fontSize: rem(20), lineHeight: '1.4' },
      h4: { fontSize: rem(16), lineHeight: '1.4' },
    },
  },

  spacing: {
    xs: rem(4),
    sm: rem(8),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },

  radius: {
    xs: rem(2),
    sm: rem(4),
    md: rem(8),
    lg: rem(12),
    xl: rem(16),
  },

  defaultRadius: 'md',

  components: {
    Button: {
      defaultProps: {
        size: 'sm',
      },
    },
    TextInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    Select: {
      defaultProps: {
        size: 'sm',
      },
    },
    Table: {
      defaultProps: {
        highlightOnHover: true,
        withTableBorder: true,
        withColumnBorders: false,
      },
    },
    Badge: {
      defaultProps: {
        size: 'sm',
        variant: 'light',
      },
    },
    Card: {
      defaultProps: {
        padding: 'md',
        radius: 'md',
        withBorder: true,
      },
    },
    Drawer: {
      defaultProps: {
        size: 'lg',
        position: 'right',
      },
    },
    Modal: {
      defaultProps: {
        centered: true,
        radius: 'md',
      },
    },
    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
  },

  other: {
    // Custom theme variables for the app
    sidebarWidth: 260,
    headerHeight: 60,

    // Status color mappings
    taskStatus: {
      queued: 'gray',
      approved: 'blue',
      scheduled: 'cyan',
      executing: 'yellow',
      verifying: 'violet',
      completed: 'green',
      failed: 'red',
      timeout: 'orange',
      cancelled: 'gray',
    },

    driftSeverity: {
      none: 'green',
      low: 'yellow',
      medium: 'orange',
      high: 'red',
    },

    dataQuality: {
      good: 'green',
      warning: 'yellow',
      poor: 'red',
    },

    identityConfidence: {
      high: 'green',
      medium: 'yellow',
      low: 'red',
    },
  },
});
